<template>
    <li
        ref="linkRef"
        role="tab"
        class="flex flex-row items-center gap-1.5 cursor-pointer font-semibold px-1.5 leading-none py-2.5 shrink-0 tabular-nums focus-visible:-outline-offset-2 focus-visible:outline-2 focus-visible:outline-sky-700 rounded-t-sm"
        :class="[
            { 'bottom-line-overdraw': props.isActive },
            props.isActive ? 'text-gray-700 bg-gray-50 shadow-[0_0_4px_rgba(0,0,0,0.25)]' : 'text-gray-500',
        ]"
        :aria-controls="props.tabPanelId"
        :tabindex="props.isActive ? 0 : -1"
        :aria-selected="props.isActive"
        @click="emit('focus-tab', true)"
        @keyup="handleKeys"
    >
        <TileRowIcon v-if="props.icon === 'Tiles'" class="size-4 text-gray-500" />
        <FormattedListIcon v-if="props.icon === 'Feed'" class="size-4 text-gray-500" />

        <slot />
    </li>
</template>

<script setup lang="ts">
import { useFocus } from '@vueuse/core';
import { ref, watchEffect } from 'vue';
import FormattedListIcon from '../Icon/FormattedListIcon.vue';
import TileRowIcon from '../Icon/TileRowIcon.vue';

export type CardTabNavigationItemProps = {
    tabPanelId: string;
    isFocused: boolean;
    isManualFocusChange: boolean;
    isActive: boolean;
    icon: 'Tiles' | 'Feed';
};

const props = defineProps<CardTabNavigationItemProps>();

const emit = defineEmits<{
    (e: 'focus-first-tab'): void;
    (e: 'focus-last-tab'): void;
    (e: 'focus-previous-tab'): void;
    (e: 'focus-next-tab'): void;
    (e: 'focus-tab', isManualFocusChange: boolean): void;
    (e: 'focus-tab-panel'): void;
}>();

const linkRef = ref();
const { focused } = useFocus(linkRef);

// Set initial focus depending on the initially opened tab
watchEffect(() => {
    if (props.isActive) {
        emit('focus-tab', false);
    }
});

// Update focus when moving between tabs
watchEffect(() => {
    if (props.isFocused && props.isManualFocusChange) {
        focused.value = true;
    }
});

function handleKeys(event: KeyboardEvent) {
    switch (event.key) {
        case 'ArrowLeft':
            emit('focus-previous-tab');
            event.preventDefault();
            break;

        case 'ArrowRight':
            emit('focus-next-tab');
            event.preventDefault();
            break;

        case 'PageUp':
            emit('focus-first-tab');
            event.preventDefault();
            break;

        case 'PageDown':
            emit('focus-last-tab');
            event.preventDefault();
            break;

        case 'Enter':
            if (!props.isActive) {
                // Activate the tab if it's not already active
                emit('focus-tab', true);
            }
            // Jump to the tab panel content
            emit('focus-tab-panel');
            event.preventDefault();
            break;
    }
}
</script>

<style>
.bottom-line-overdraw {
    position: relative;
}

.bottom-line-overdraw::after {
    position: absolute;
    bottom: -0.3125rem;
    background-color: var(--color-gray-50);
    left: 0;
    right: 0;
    height: 0.3125rem;
    width: 100%;
    content: '';
}
</style>
